// Uma linha da tabela-comanda da Ficha Única (Fase 6): um Tipo de Peça, as
// quatro colunas lado a lado — Recebida, Conferida, Devolvida, Retida — com o
// motivo de divergência e os motivos de retenção escondidos num painel que só
// abre quando a linha precisa deles. Reúne o que hoje eram dois lugares
// separados — a Contagem Recebida do lançamento e o bloco de Fechamento — na
// mesma linha, reaberta ao longo de toda a vida da OS.

import { codigoSublote, type SaldoLinha } from "../../domain/backlog.ts";
import type { PreviaTipo, RecusaFechamento } from "../../domain/devolucao.ts";
import type { Id } from "../../domain/ids.ts";
import type { Trecho } from "../../domain/situacao-retencao.ts";
import type { ItemCategoria } from "./_resumo-categoria.ts";
import {
  campoConferida,
  campoRetida,
  desenhoDaPrevia,
  divergencia,
  impedimentoDaLinha,
  semZero,
  sugestaoMotivo,
  type EstadoLinha,
} from "./_linha-peca.ts";
import type { Store } from "../store.ts";
import { clear, el, botao, situacaoComIcone } from "../ui.ts";
import { listaSelect } from "./_lista-select.ts";

interface RetidaLinha {
  motivo: string;
  quantidade: number;
}

export interface ContagemColhida {
  tipoId: Id;
  recebida: number;
  devolvida: number;
  retidas: RetidaLinha[];
  /** `null` = sem divergência (Conferida omitida ou igual à Recebida). */
  conferida: number | null;
  conferidaMotivo: string | null;
  retidaInformada: number | null;
}

export interface LinhaTipoPeca {
  readonly element: HTMLElement;
  readonly tipoId: Id;
  /** Lê o estado atual das quatro colunas, pronto pro comando de Fechamento. */
  colher(): ContagemColhida;
  /** Zera Devolvida e Retidas; Conferida volta a bater com a Recebida. */
  limpar(): void;
  /** Lança a Devolvida igual à Recebida, sem retidas nem divergência. */
  aplicarComanda(): void;
  /** A Recebida e as três colunas de Fechamento travam em momentos
   * diferentes da vida da ficha — por isso dois booleanos, não um. */
  travar(opts: { recebidaTravada: boolean; fechamentoTravado: boolean }): void;
  /** Verdadeiro quando a Conferida diverge da Recebida e falta escolher o motivo. */
  pendente(): boolean;
  /** Quanto desta linha veio do Backlog nesta Devolução (0 fora de uma OS lançada). */
  excedenteAtual(): number;
  /** A linha para o resumo da categoria e dos totais da Ficha — com o que
   * impede o "Fechar OS" nela, se houver. */
  resumo(): ItemCategoria;
  /**
   * O que a Prévia do Fechamento (#83/#84) diz desta peça — conta, carona,
   * situação — e as recusas dela, na ordem do "Fechar OS". A linha não
   * calcula nada do Fechamento: só desenha isto. `null` sem Lote.
   */
  aplicarPrevia(previa: PreviaTipo | null, recusas: readonly RecusaFechamento[]): void;
}

export interface OpcoesLinhaTipoPeca {
  store: Store;
  /** `null` numa OS ainda sem Lote — sem Lote não há Fechamento a prever. */
  loteId: Id | null;
  tipo: { id: Id; nome: string };
  gravado?: {
    recebida: number;
    devolvida: number;
    conferida: number | null;
    conferidaMotivo: string | null;
    retidaInformada: number | null;
  };
  retidasGravadas: readonly RetidaLinha[];
  /** Roda a cada edição de qualquer coluna — a ficha usa para recalcular
   * travamento de botões e totais. */
  aoMudar(): void;
}


/** Os trechos da situação da peça, com os números em negrito. */
function comDestaque(trechos: readonly Trecho[]): (Node | string)[] {
  return trechos.map((t) => (typeof t === "string" ? t : el("b", { class: "num" }, String(t.n))));
}

export function criarLinhaTipoPeca(opts: OpcoesLinhaTipoPeca): LinhaTipoPeca {
  const { store, loteId, tipo } = opts;
  const recebidaIni = opts.gravado?.recebida ?? 0;
  // Só uma Devolução já gravada com divergência real tem motivo pra manter a
  // Conferida "presa": sem isso, uma linha nova (Coleta) ou sem divergência
  // salva ficava com Conferida travada em "0" enquanto o campo está desabilitado,
  // e a Recebida digitada durante a Coleta "divergia" contra esse zero — abria
  // o painel de motivo na hora, para um campo que nem existe ainda nessa etapa.
  const conferidaEraDivergente = opts.gravado?.conferida != null;

  const recebida = el("input", {
    type: "number",
    min: "0",
    step: "1",
    placeholder: "0",
    "aria-label": `Recebida de ${tipo.nome}`,
  }) as HTMLInputElement;
  recebida.value = recebidaIni > 0 ? String(recebidaIni) : "";

  const conferida = el("input", {
    type: "number",
    min: "0",
    step: "1",
    placeholder: "0",
    "aria-label": `Conferida de ${tipo.nome}`,
  }) as HTMLInputElement;
  // quantidade zero fica vazia (o "0" é só a dica, que some ao clicar) —
  // menos a Conferida GRAVADA como 0, que é uma divergência de verdade
  conferida.value =
    opts.gravado?.conferida != null
      ? String(opts.gravado.conferida)
      : semZero(opts.gravado?.recebida ?? recebidaIni);
  conferida.disabled = true;

  const devolvida = el("input", {
    type: "number",
    min: "0",
    step: "1",
    placeholder: "0",
    "aria-label": `Devolvida de ${tipo.nome}`,
  }) as HTMLInputElement;
  if (opts.gravado && opts.gravado.devolvida > 0) devolvida.value = String(opts.gravado.devolvida);
  devolvida.disabled = true;

  const retidaTotal = el("input", {
    type: "number",
    placeholder: "0",
    "aria-label": `Retida de ${tipo.nome}, total`,
  }) as HTMLInputElement;
  retidaTotal.disabled = false;
  // O campo "Retida" é a Retida digitada da Comanda (`retidaInformada`) — o
  // Backlog do hotel daquela peça —, editável enquanto a Coleta está aberta.
  let retidaInformadaValor: number | null = opts.gravado?.retidaInformada ?? null;

  let retidas: RetidaLinha[] = opts.retidasGravadas.map((r) => ({ ...r }));
  let fechamentoAberto = false;

  /** O que está digitado na linha agora — a entrada das regras de `_linha-peca.ts`. */
  const estado = (): EstadoLinha => ({
    recebida: Number(recebida.value) || 0,
    conferida: conferida.value,
    conferidaTravada: conferida.disabled,
    conferidaEraDivergente,
    motivoDivergencia: motivoDiv.value(),
    devolvida: Number(devolvida.value) || 0,
    retidas,
    retidaInformada: retidaInformadaValor,
    fechamentoAberto,
  });
  const mostrarRetida = (): void => {
    retidaTotal.value = campoRetida(estado());
  };

  const motivoDiv = listaSelect(
    store,
    "motivosDivergencia",
    opts.gravado?.conferidaMotivo ?? "",
  );
  const motivoWrap = el(
    "div",
    { class: "ficha-motivo-divergencia" },
    el("label", {}, "Motivo da Divergência", motivoDiv.element),
  );
  motivoWrap.hidden = true;

  const retidasEl = el("div", { class: "ficha-retidas-linhas" });
  const addRetida = botao("link", {}, "+ Motivo de Retenção");

  const origemEl = el("span", { class: "ficha-origem-backlog" });
  // Como fica o Backlog do hotel depois desta devolução (conta) e o primeiro
  // problema que impede o "Fechar OS" na retenção (situação) — ADR-0029.
  const contaEl = el("span", { class: "ficha-backlog-hotel" });
  const situacaoEl = el("div", { class: "ficha-situacao-retencao" });

  const caret = el(
    "button",
    {
      type: "button",
      class: "ficha-caret",
      "aria-expanded": "false",
      "aria-label": `Detalhar Motivos de Retenção de ${tipo.nome}`,
    },
    "▾",
  );

  const detalhe = el(
    "div",
    { class: "ficha-detalhe" },
    motivoWrap,
    el("div", { class: "ficha-retidas-bloco" }, retidasEl, addRetida),
    el(
      "div",
      { class: "ficha-rodape" },
      el("div", { class: "ficha-rodape-origem" }, contaEl, situacaoEl, origemEl),
    ),
  );

  const detalheAberto = (): boolean => detalhe.classList.contains("aberto");
  const alternarDetalhe = (aberto: boolean): void => {
    detalhe.classList.toggle("aberto", aberto);
    caret.setAttribute("aria-expanded", String(aberto));
    caret.textContent = aberto ? "▴" : "▾";
  };

  const desenharRetidas = (): void => {
    clear(retidasEl);
    retidas.forEach((r, i) => {
      const motivoSel = listaSelect(store, "motivosRetencao", r.motivo);
      const sel = motivoSel.element.querySelector("select") as HTMLSelectElement;
      sel.setAttribute("aria-label", `Motivo da retenção de ${tipo.nome}`);
      sel.addEventListener("change", () => {
        r.motivo = sel.value;
        atualizar();
      });
      const qtd = el("input", {
        type: "number",
        min: "1",
        step: "1",
        "aria-label": "Quantidade retida",
      }) as HTMLInputElement;
      qtd.value = String(r.quantidade);
      qtd.addEventListener("input", () => {
        r.quantidade = Number(qtd.value) || 0;
        atualizar();
      });
      const rm = botao(
        { variante: "secundario", tamanho: "compacto" },
        { "aria-label": "Remover Motivo" },
        "×",
      );
      rm.addEventListener("click", () => {
        retidas.splice(i, 1);
        desenharRetidas();
        atualizar();
      });
      retidasEl.append(
        el("div", { class: "ficha-retida-linha" }, motivoSel.element, qtd, rm),
      );
    });
  };

  addRetida.addEventListener("click", () => {
    if (addRetida.hasAttribute("disabled")) return;
    // Sugere o máximo que ainda cabe: o Backlog do hotel depois desta
    // devolução menos o que já tem motivo — peça que fica é peça nova (as da
    // OS anterior voltaram relavadas). Sem sobra, 1.
    retidas.push({ motivo: "", quantidade: sugestaoMotivo(previaAtual) });
    desenharRetidas();
    alternarDetalhe(true);
    atualizar();
  });

  let pendenteAtual = false;
  /** O que a Prévia do Fechamento diz desta peça, e as recusas dela. */
  let previaAtual: PreviaTipo | null = null;
  let recusasAtuais: readonly RecusaFechamento[] = [];

  /** De onde sai a carona: "↳ do Backlog: 2 do saldo inicial, 1 do sublote …". */
  const textoOrigem = (origem: readonly SaldoLinha[]): string => {
    const lotes = store.get().lotes;
    return origem
      .map((o) =>
        o.origemLoteId === null
          ? `${o.quantidade} do saldo inicial`
          : `${o.quantidade} do sublote ${codigoSublote({ codigo: lotes.find((l) => l.id === o.origemLoteId)?.codigo ?? "?" })}`,
      )
      .join(", ");
  };

  // Desenha a Prévia: o que mostrar vem de `desenhoDaPrevia` (_linha-peca.ts);
  // aqui só vira DOM, com destaque nos números.
  const desenharPrevia = (): void => {
    clear(contaEl);
    clear(situacaoEl);
    clear(origemEl);
    const d = desenhoDaPrevia(estado(), previaAtual, recusasAtuais);
    if (!d) return;
    if (d.origem.length > 0) origemEl.append(`↳ do Backlog: ${textoOrigem(d.origem)}`);
    if (d.conta) contaEl.append(...comDestaque(d.conta));
    for (const s of d.situacao) situacaoEl.append(el("div", {}, situacaoComIcone(s.tom, ...comDestaque(s.trechos))));
    if (d.alerta) {
      caret.classList.add("ficha-caret-alerta");
      if (!detalheAberto()) alternarDetalhe(true);
    }
  };

  const atualizar = (): void => {
    mostrarRetida();
    if (conferida.disabled) conferida.value = campoConferida(estado());
    const { divergente, pendente } = divergencia(estado());
    motivoWrap.hidden = !divergente;
    pendenteAtual = pendente;
    caret.classList.toggle("ficha-caret-alerta", pendenteAtual);
    if (divergente && !detalheAberto()) alternarDetalhe(true);

    // a Ficha recalcula a Prévia da OS inteira e chama `aplicarPrevia`
    opts.aoMudar();
  };
  recebida.addEventListener("input", atualizar);
  conferida.addEventListener("input", atualizar);
  devolvida.addEventListener("input", atualizar);
  retidaTotal.addEventListener("input", () => {
    if (recebida.disabled) return;
    retidaInformadaValor = Number(retidaTotal.value) || null;
    atualizar();
  });
  motivoDiv.element.querySelector("select")!.addEventListener("change", atualizar);
  caret.addEventListener("click", () => alternarDetalhe(!detalheAberto()));

  desenharRetidas();
  atualizar();

  const element = el(
    "div",
    { class: "ficha-linha-tipo" },
    el(
      "div",
      { class: "ficha-linha-cab" },
      el("span", { class: "ficha-peca-nome" }, tipo.nome),
      el("span", { class: "ficha-campo ficha-campo-recebida" }, recebida),
      el("span", { class: "ficha-campo" }, conferida),
      el("span", { class: "ficha-campo" }, devolvida),
      el("span", { class: "ficha-campo ficha-campo-retida" }, retidaTotal, caret),
    ),
    detalhe,
  );

  const colher = (): ContagemColhida => {
    const rec = Number(recebida.value) || 0;
    const { divergente } = divergencia(estado());
    const confNum = Number(conferida.value.trim());
    return {
      tipoId: tipo.id,
      recebida: rec,
      devolvida: Number(devolvida.value) || 0,
      retidas: retidas.filter((r) => r.quantidade > 0).map((r) => ({ ...r })),
      conferida: divergente ? confNum : null,
      conferidaMotivo: divergente ? motivoDiv.value().trim() || null : null,
      retidaInformada: retidaInformadaValor,
    };
  };

  return {
    element,
    tipoId: tipo.id,
    colher,
    limpar: () => {
      retidas = [];
      devolvida.value = "";
      conferida.value = recebida.value;
      desenharRetidas();
      atualizar();
    },
    aplicarComanda: () => {
      retidas = [];
      devolvida.value = recebida.value && Number(recebida.value) > 0 ? recebida.value : "";
      conferida.value = recebida.value;
      desenharRetidas();
      atualizar();
    },
    travar: ({ recebidaTravada, fechamentoTravado }) => {
      recebida.disabled = recebidaTravada;
      retidaTotal.disabled = recebidaTravada; // a Retida digitada é da Coleta: trava junto com ela
      mostrarRetida();
      conferida.disabled = fechamentoTravado;
      devolvida.disabled = fechamentoTravado;
      addRetida.toggleAttribute("disabled", fechamentoTravado);
      motivoDiv.element.querySelector("select")!.toggleAttribute("disabled", fechamentoTravado);
      retidasEl.querySelectorAll("input, select, button").forEach((elx) => {
        (elx as HTMLInputElement | HTMLSelectElement | HTMLButtonElement).disabled =
          fechamentoTravado;
      });
      fechamentoAberto = !fechamentoTravado;
      desenharPrevia();
    },
    pendente: () => pendenteAtual,
    excedenteAtual: () => previaAtual?.conta.doBacklog ?? 0,
    resumo: () => {
      const c = colher();
      // os números são os da Prévia (a conta do "Fechar OS"); sem Lote ainda, só a Coleta
      const conta = previaAtual?.conta;
      return {
        recebida: c.recebida,
        rende: conta?.rende ?? c.recebida,
        devolvida: conta?.devolvida ?? c.devolvida,
        retidas: conta?.retida ?? c.retidas.reduce((a, r) => a + r.quantidade, 0),
        semDestino: conta?.semDestino ?? 0,
        impedimento: impedimentoDaLinha({ pendente: pendenteAtual, fechamentoAberto, previa: previaAtual, recusas: recusasAtuais }),
      };
    },
    aplicarPrevia: (previa, recusas) => {
      previaAtual = previa;
      recusasAtuais = recusas;
      desenharPrevia();
    },
  };
}
